import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { ApiError } from '@/api/client'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import {
  METHOD_LABELS,
  PAYMENT_METHODS,
  formatMoney,
  newIdempotencyKey,
  recordPayment,
  type Invoice,
  type PaymentMethod,
} from '@/features/invoices/api'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

interface Form {
  amount: string
  method: PaymentMethod
  reference: string
  notes: string
}

const emptyForm = (balance: string): Form => ({
  // Settling the invoice in full is what happens most of the time, so it is what the field
  // starts on. Anything else is a deliberate edit rather than a number to be typed twice.
  amount: balance,
  method: 'cash',
  reference: '',
  notes: '',
})

/**
 * Record a payment against an invoice.
 *
 * The interesting part is the idempotency key, which is what stops a rep on a bad
 * connection from recording the same money twice.
 *
 * The key is minted here and held in state, not generated at submit time: two taps on a
 * request that has not answered yet must carry the *same* key, or the server has no way to
 * tell a retry from a second payment. It is replaced whenever the form changes and whenever
 * the dialog is opened afresh -- the first because the API answers a reused key with a 409
 * if the body differs, the second because a customer paying £50 twice in one afternoon is
 * two payments and must not be collapsed into one.
 */
export function RecordPaymentDialog({
  invoice,
  open,
  onClose,
}: {
  invoice: Invoice
  open: boolean
  onClose: () => void
}) {
  const [form, setForm] = useState<Form>(() => emptyForm(invoice.balance_due))
  const [key, setKey] = useState(newIdempotencyKey)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!open) return
    setForm(emptyForm(invoice.balance_due))
    setKey(newIdempotencyKey())
  }, [open, invoice.balance_due])

  /** Every edit invalidates the key, because it is a key for *this* payment, not this form. */
  const set = (patch: Partial<Form>) => {
    setForm((current) => ({ ...current, ...patch }))
    setKey(newIdempotencyKey())
  }

  const submit = useMutation({
    mutationFn: () =>
      recordPayment(
        invoice.id,
        {
          amount: form.amount,
          method: form.method,
          reference: form.reference.trim() || null,
          notes: form.notes.trim() || null,
        },
        key,
      ),
    onSuccess: (payment) => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', invoice.id] })
      void queryClient.invalidateQueries({ queryKey: ['invoices'] })
      void queryClient.invalidateQueries({ queryKey: ['invoice-aging'] })
      toast.success(`Recorded ${formatMoney(payment.amount)}.`)
      onClose()
    },
    // A refusal here is a business answer -- more than the balance, invoice still a draft --
    // so the server's wording is shown as it stands rather than replaced with a vaguer one.
    onError: (error: unknown) =>
      toast.error(
        error instanceof ApiError
          ? error.message
          : 'Could not reach the server. Nothing was recorded.',
      ),
  })

  const amount = Number(form.amount)
  const balance = Number(invoice.balance_due)
  const invalid =
    !form.amount || Number.isNaN(amount) || amount <= 0 ? 'Enter an amount above zero.' : undefined
  const over = !invalid && amount > balance

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Record a payment on ${invoice.invoice_number}`}
      description={`${formatMoney(invoice.balance_due)} outstanding of ${formatMoney(
        invoice.total,
      )}.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={submit.isPending}
            disabled={Boolean(invalid) || over}
            onClick={() => submit.mutate()}
          >
            Record payment
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label="Amount"
          error={invalid}
          hint={over ? undefined : 'Less than the balance records a part payment.'}
        >
          {(props) => (
            <Input
              {...props}
              type="number"
              inputMode="decimal"
              min="0.01"
              step="0.01"
              value={form.amount}
              onChange={(event) => set({ amount: event.target.value })}
            />
          )}
        </Field>

        {over && (
          <p role="alert" className="text-sm text-danger">
            That is more than the {formatMoney(invoice.balance_due)} outstanding. The API refuses an
            overpayment, so reduce it or raise a credit note instead.
          </p>
        )}

        <Field label="Method">
          {(props) => (
            <select
              {...props}
              className={SELECT_CLASS}
              value={form.method}
              onChange={(event) => set({ method: event.target.value as PaymentMethod })}
            >
              {PAYMENT_METHODS.map((method) => (
                <option key={method} value={method}>
                  {METHOD_LABELS[method]}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field label="Reference" hint="Cheque number, transfer reference, receipt number.">
          {(props) => (
            <Input
              {...props}
              value={form.reference}
              maxLength={100}
              placeholder="Optional"
              onChange={(event) => set({ reference: event.target.value })}
            />
          )}
        </Field>

        <Field label="Notes">
          {(props) => (
            <Input
              {...props}
              value={form.notes}
              maxLength={500}
              placeholder="Optional"
              onChange={(event) => set({ notes: event.target.value })}
            />
          )}
        </Field>
      </div>
    </Modal>
  )
}
